/**
 * Router Base - Routing per Gleem Core
 * Sistema modulare: ogni modulo può registrare le sue routes
 * Include RBAC (Role-Based Access Control)
 */

import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { getActivePinia } from 'pinia'
import { useAuthStore, type UserRole } from '@/core/stores/auth'

// Import module routes
import { ebRoutes } from '@/modules/eb/router'

/**
 * Permessi route basati su prefisso path
 * Se una route non è nella lista, è accessibile a tutti gli utenti autenticati
 */
type RoutePermission = {
  pathPrefix: string
  roles: UserRole[]
}

const FULL_ACCESS: UserRole[] = ['admin', 'manager', 'office']
const ADMIN_ONLY: UserRole[] = ['admin']

const routePermissions: RoutePermission[] = [
  // Contabilità - solo admin, manager, office
  { pathPrefix: '/eb/accounting', roles: FULL_ACCESS },
  // Centri di costo (budget/consuntivi) - solo admin, manager, office
  { pathPrefix: '/eb/cost-centers', roles: FULL_ACCESS },

  // Commerciale - solo admin, manager, office
  { pathPrefix: '/eb/sales', roles: FULL_ACCESS },
  { pathPrefix: '/eb/purchases', roles: FULL_ACCESS },
  { pathPrefix: '/eb/purchase', roles: FULL_ACCESS }, // legacy

  // Fornitori, Prodotti, Magazzino, Dipendenti - solo admin, manager, office
  { pathPrefix: '/eb/suppliers', roles: FULL_ACCESS },
  { pathPrefix: '/eb/warehouse', roles: FULL_ACCESS },
  { pathPrefix: '/eb/stock', roles: FULL_ACCESS },
  { pathPrefix: '/eb/employees', roles: FULL_ACCESS },

  // CRM - solo admin, manager, office
  { pathPrefix: '/eb/crm', roles: FULL_ACCESS },
  { pathPrefix: '/eb/call-logs', roles: FULL_ACCESS },
  { pathPrefix: '/eb/tasks', roles: FULL_ACCESS },

  // Report - solo admin, manager, office
  { pathPrefix: '/eb/reports', roles: FULL_ACCESS },

  // Impostazioni - solo admin
  { pathPrefix: '/eb/settings/users', roles: ADMIN_ONLY },
  { pathPrefix: '/eb/settings', roles: ADMIN_ONLY },
  { pathPrefix: '/eb/activity-log', roles: ADMIN_ONLY },

]

/**
 * Verifica se un utente con un dato ruolo può accedere a una route
 */
function canAccessRoute(path: string, userRole: UserRole | undefined): boolean {
  if (!userRole) return false

  // Trova la regola più specifica (path più lungo)
  const matchingRules = routePermissions
    .filter(p => path.startsWith(p.pathPrefix))
    .sort((a, b) => b.pathPrefix.length - a.pathPrefix.length)

  if (matchingRules.length === 0) {
    // Nessuna regola specifica = tutti i ruoli autenticati possono accedere
    return true
  }

  // Usa la regola più specifica
  return matchingRules[0].roles.includes(userRole)
}

function shouldReturnToLandingAfterLogout(): boolean {
  try {
    const rawLogoutAt = sessionStorage.getItem('gleem_logout_at')
    const logoutAt = rawLogoutAt ? Number(rawLogoutAt) : 0
    const isRecentLogout = logoutAt > 0 && Date.now() - logoutAt < 5000

    if (rawLogoutAt && !isRecentLogout) {
      sessionStorage.removeItem('gleem_logout_at')
    }

    return isRecentLogout
  } catch {
    return false
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/core/views/LoginPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    name: 'Landing',
    component: () => import('@/core/views/LandingPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/core/views/Auth/ResetPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: () => import('@/core/views/Auth/ChangePassword.vue'),
    meta: { requiresAuth: true, title: 'Cambia password' }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/core/views/Auth/AuthCallback.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/eb',
    component: () => import('@/core/components/layout/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      // Module routes (EB)
      // Note: ebRoutes define absolute paths starting with /eb
      ...ebRoutes
    ]
  },
  {
    // Blocco abbonamento: fuori da /eb (nessun layout, nessuna RBAC di modulo).
    // Ci si arriva dal 403 SUBSCRIPTION_EXPIRED intercettato in api.ts.
    path: '/subscription-expired',
    name: 'SubscriptionExpired',
    component: () => import('@/core/views/SubscriptionExpired.vue'),
    meta: { requiresAuth: true, title: 'Abbonamento' }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// =============================================================================
// PWA chunk recovery: quando il browser ha l'HTML cached da un build precedente
// e prova a caricare un chunk async (`import('../views/...')`) il cui hash non
// esiste più sul server, otteniamo un ChunkLoadError. Senza recovery la SPA
// crasha o mostra "Connessione persa". Qui:
//  - rileviamo il pattern con regex tollerante su message/name
//  - facciamo UN solo reload con sessionStorage guard per evitare loop infiniti
//  - se il reload non risolve (chunk genuinamente rotto), lasciamo passare
//    l'errore ai gestori superiori dopo aver consumato il guard
// =============================================================================
const CHUNK_RELOAD_GUARD = 'gleem_chunk_reload_at'
const CHUNK_RELOAD_TTL_MS = 30 * 1000 // 30s: oltre, autorizziamo un altro reload

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false
  const message = (err as Error)?.message || String(err)
  const name = (err as Error)?.name || ''
  // Vite/Webpack/native ESM emettono messaggi tipo:
  //   "Failed to fetch dynamically imported module: https://.../assets/X.js"
  //   "Importing a module script failed."
  //   ChunkLoadError (webpack)
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk \S+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  )
}

function attemptChunkRecovery(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false
  try {
    const lastAt = Number(sessionStorage.getItem(CHUNK_RELOAD_GUARD) || '0')
    const now = Date.now()
    if (now - lastAt < CHUNK_RELOAD_TTL_MS) {
      console.error('[router] Chunk error after recent reload, giving up to avoid loop', err)
      return false
    }
    sessionStorage.setItem(CHUNK_RELOAD_GUARD, String(now))
    console.warn('[router] Chunk load error detected, reloading to refresh build', err)
    window.location.reload()
    return true
  } catch {
    window.location.reload()
    return true
  }
}

router.onError((err) => {
  attemptChunkRecovery(err)
})

// Anche le promise rejection globali (dynamic import iniziali, fetch dei
// chunk async fuori dal router) devono triggerare il recovery.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (attemptChunkRecovery(event.reason)) {
      event.preventDefault()
    }
  })
  window.addEventListener('error', (event) => {
    if (attemptChunkRecovery(event.error || event.message)) {
      event.preventDefault()
    }
  })
}

// Route guards
router.beforeEach(async (to, from, next) => {
  const activePinia = getActivePinia()
  if (!activePinia) {
    console.warn('[router] Pinia not ready, skipping auth guard')
    next()
    return
  }

  const authStore = useAuthStore(activePinia)

  // Update document title
  if (to.meta.title) {
    document.title = `${to.meta.title} - Gleem Core`
  }

  // 1. Check authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    if (shouldReturnToLandingAfterLogout()) {
      next({ name: 'Landing' })
      return
    }

    next({ name: 'Login', query: { redirect: to.fullPath } })
    return
  }

  // 2. Redirect authenticated users away from login and landing
  if ((to.name === 'Login' || to.name === 'Landing') && authStore.isAuthenticated) {
    next({ name: 'EB-Dashboard' })
    return
  }

  // 2b. Password provvisoria: finche' non la cambia, l'utente vede solo quella pagina
  if (authStore.isAuthenticated && to.name !== 'ChangePassword') {
    await authStore.waitForUser()
    if (authStore.user?.must_change_password) {
      next({ name: 'ChangePassword' })
      return
    }
  }

  // 3. Check role-based access (RBAC)
  if (authStore.isAuthenticated && to.path.startsWith('/eb')) {
    // Attendi che l'utente sia caricato prima di verificare i permessi
    await authStore.waitForUser()

    const userRole = authStore.user?.role
    if (!canAccessRoute(to.path, userRole)) {
      console.warn(`[RBAC] Accesso negato a ${to.path} per ruolo ${userRole}`)
      // Redirect to dashboard with access denied message
      next({ name: 'EB-Dashboard', query: { accessDenied: '1' } })
      return
    }
  }

  next()
})

export default router
